import { useEffect } from 'react'

export interface ISelectedBank {
    saderat: boolean
    parsian: boolean
}

export interface IGateway {
    is_default: boolean
    is_active: boolean
    is_in_mobile_visible: boolean
    terminal_type: string
    unique_code: string
    name?: string
    img_url?: string
}

interface IData {
    gateways?: IGateway[]
}

/** کارت‌به‌کارت — the gateway that works for every fund. */
export const CARD_TO_CARD = 'HAM_IPG'

/** Instant bank gateway, but allowlisted to the funds Pasargad is live on. */
export const PASARGAD = 'PSG_IPG'

/**
 * Gateways this client is willing to show, most-preferred first.
 *
 * `is_in_mobile_visible` used to be a hard filter, which is what walled off
 * every fund outside the Pasargad allowlist. On those funds the backend
 * returns `HAM_IPG` alone — and `HAM_IPG.is_in_mobile_visible` is false — so
 * the picker rendered ZERO options and the pay button could only ever toast
 * «درگاه پرداخت را انتخاب کنید». Meanwhile the payables screen, which asks for
 * the gateway list without naming a fund, kept Pasargad and sent people into a
 * 400. Same missing gateway, two different-looking dead ends.
 *
 * So کارت‌به‌کارت is opted in by unique_code rather than by the flag. It is
 * deliberately an allowlist and not "drop the filter": the flag still keeps
 * anything else that is switched off for mobile (a gateway under test, say)
 * out of the picker.
 */
const ALWAYS_SHOW = [CARD_TO_CARD]

const isShowable = (gateway: IGateway) =>
    gateway.terminal_type === 'htrm' &&
    gateway.is_active &&
    (gateway.is_in_mobile_visible || ALWAYS_SHOW.includes(gateway.unique_code))

/**
 * Order: the instant gateway first when it is available, کارت‌به‌کارت after.
 *
 * Not `is_default` first — that is a global flag and the list is per-fund, so
 * on the ~29k funds Pasargad is not live on, `is_default` picks nothing and
 * the order fell back to whatever the API happened to return.
 */
const rank = (gateway: IGateway) =>
    gateway.unique_code === CARD_TO_CARD ? 1 : gateway.is_default ? 0 : 0.5

export const sortGateways = (gateways: IGateway[]): IGateway[] =>
    [...gateways].sort((a, b) => rank(a) - rank(b))

/** Legacy hook kept for backward compat with pages not yet migrated. */
export const useIsDefaultGateway = (
    data: IData | undefined,
    setIsSelected: React.Dispatch<React.SetStateAction<ISelectedBank>>
) => {
    useEffect(() => {
        if (data && data.gateways) {
            const parsianDefault = data.gateways[0]?.is_default || false
            const saderatDefault = data.gateways[1]?.is_default || false
            setIsSelected({ parsian: parsianDefault, saderat: saderatDefault })
        } else {
            setIsSelected({ parsian: false, saderat: false })
        }
    }, [data, setIsSelected])
}

/** Gateways to show for an `init_pay`-shaped payload. */
export const getActiveGateways = (data: IData | undefined): IGateway[] =>
    sortGateways((data?.gateways ?? []).filter(isShowable))

/** Sets selectedCode to the first gateway we would show. */
export const useActiveGatewayCode = (
    data: IData | undefined,
    setCode: React.Dispatch<React.SetStateAction<string | null>>
) => {
    useEffect(() => {
        if (data?.gateways) {
            setCode(getActiveGateways(data)[0]?.unique_code ?? null)
        }
    }, [data])
}
